import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Instagram, Linkedin, Youtube } from 'lucide-react';
import { CONTACT_INFO, SOCIAL_LINKS } from '../../utils/constants';

const Footer = () => {
  const year = new Date().getFullYear();

  return (
    <footer style={{ background: '#0d1b2e' }}>
      <div className="container-custom py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">

          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-5">
              <img src="/assets/logo.png" alt="Platinum Healthcare" className="h-14 w-auto object-contain" />
            </Link>
            <p className="text-sm leading-relaxed mb-6" style={{ color: 'rgba(255,255,255,0.4)' }}>
              Providing exceptional healthcare services with compassion and expertise across Dubai and Ras Al Khaimah.
            </p>
            <div className="flex gap-3">
              {[
                { href: SOCIAL_LINKS.instagram, Icon: Instagram, label: 'Instagram' },
                { href: SOCIAL_LINKS.facebook,  Icon: Facebook,  label: 'Facebook' },
                { href: SOCIAL_LINKS.linkedin,  Icon: Linkedin,  label: 'LinkedIn' },
                { href: SOCIAL_LINKS.youtube,   Icon: Youtube,   label: 'YouTube' },
              ].map(({ href, Icon, label }) => (
                <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label}
                  className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                  style={{ background: 'rgba(8,145,178,0.12)', border: '1px solid rgba(8,145,178,0.25)' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(8,145,178,0.3)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(8,145,178,0.12)'}
                >
                  <Icon size={14} color="#0891b2" />
                </a>
              ))}
            </div>
          </div>

          {/* Navigation */}
          <div>
            <p className="text-xs font-bold uppercase tracking-widest mb-5" style={{ color: '#0891b2' }}>Navigation</p>
            <div className="flex flex-col gap-3">
              {[['Home','/'],['About','/about'],['Specialties','/departments'],['Doctors','/doctors'],['Contact','/contact']].map(([l,p]) => (
                <Link key={p} to={p} className="text-sm transition-colors duration-200"
                  style={{ color: 'rgba(255,255,255,0.45)', textDecoration: 'none' }}
                  onMouseEnter={e => e.currentTarget.style.color = 'white'}
                  onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.45)'}
                >{l}</Link>
              ))}
            </div>
          </div>

          {/* Branches */}
          <div>
            <p className="text-xs font-bold uppercase tracking-widest mb-5" style={{ color: '#0891b2' }}>Our Branches</p>
            <div className="flex flex-col gap-4">
              {CONTACT_INFO.locations.map((loc, i) => (
                <div key={i}>
                  <p className="text-xs font-semibold mb-0.5" style={{ color: 'rgba(255,255,255,0.7)' }}>{loc.name}</p>
                  <p className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.35)' }}>{loc.address}</p>
                  <a href={`tel:${loc.phone}`} className="text-xs block mt-0.5 transition-colors duration-200"
                    style={{ color: 'rgba(8,145,178,0.8)', textDecoration: 'none' }}
                    onMouseEnter={e => e.currentTarget.style.color = '#0891b2'}
                    onMouseLeave={e => e.currentTarget.style.color = 'rgba(8,145,178,0.8)'}
                  >{loc.phone}</a>
                </div>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div>
            <p className="text-xs font-bold uppercase tracking-widest mb-5" style={{ color: '#0891b2' }}>Main Contact</p>
            <div className="flex flex-col gap-4">
              <div className="flex items-start gap-3">
                <MapPin size={13} color="#0891b2" className="mt-0.5 flex-shrink-0" />
                <span className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.45)' }}>{CONTACT_INFO.address}</span>
              </div>
              <div className="flex items-center gap-3">
                <Phone size={13} color="#0891b2" className="flex-shrink-0" />
                <div>
                  <a href={`tel:${CONTACT_INFO.phone}`} className="text-xs block transition-colors duration-200"
                    style={{ color: 'rgba(255,255,255,0.45)', textDecoration: 'none' }}
                    onMouseEnter={e => e.currentTarget.style.color = 'white'}
                    onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.45)'}
                  >{CONTACT_INFO.phone}</a>
                  <a href={`tel:${CONTACT_INFO.mobile}`} className="text-xs block transition-colors duration-200"
                    style={{ color: 'rgba(255,255,255,0.45)', textDecoration: 'none' }}
                    onMouseEnter={e => e.currentTarget.style.color = 'white'}
                    onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.45)'}
                  >{CONTACT_INFO.mobile}</a>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Mail size={13} color="#0891b2" className="flex-shrink-0" />
                <a href={`mailto:${CONTACT_INFO.email}`} className="text-xs transition-colors duration-200"
                  style={{ color: 'rgba(255,255,255,0.45)', textDecoration: 'none' }}
                  onMouseEnter={e => e.currentTarget.style.color = 'white'}
                  onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.45)'}
                >{CONTACT_INFO.email}</a>
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div style={{ height: 1, background: 'rgba(255,255,255,0.07)', marginBottom: 24 }} />

        {/* Bottom */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>
            © {year} Platinum Healthcare Group. All Rights Reserved.
          </p>
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>
            UAE Emergency: <span style={{ color: 'rgba(255,255,255,0.6)', fontWeight: 600 }}>Police — 999</span> &nbsp;|&nbsp; <span style={{ color: 'rgba(255,255,255,0.6)', fontWeight: 600 }}>Ambulance — 998</span>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
